import type {
  PickUpPaginatedResponse,
  PickUpExportBody,
} from '../../PickUp/models';
import type {
  ExportJob,
  InitiateExportResponse,
} from '@/pages/Business/pages/Monitoring/models/Export';
import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';
import { VisitApi } from '../models';

const visitsService = createApi({
  reducerPath: 'visitsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/visits`),
  tagTypes: ['Visits'],
  keepUnusedDataFor: 30,
  refetchOnMountOrArgChange: 60,
  endpoints: (builder) => ({
    getVisitsByShippingNumber: builder.query<
      PaginatedResponse<VisitApi>,
      {
        shippingNumber: string;
        paginated?: { page: number; pageSize: number };
      }
    >({
      query: ({ shippingNumber, paginated }) => ({
        url: '',
        params: {
          shippingNumber,
          includeDocuments: true,
          ...(paginated && {
            limit: paginated.pageSize,
            offset: (paginated.page - 1) * paginated.pageSize,
          }),
        },
      }),
      transformResponse: (response: {
        paging?: { count?: number; items?: VisitApi[] };
      }) => ({
        count: response?.paging?.count ?? 0,
        items: response?.paging?.items ?? [],
      }),
      providesTags: ['Visits'],
    }),
    getPickUps: builder.query<
      PickUpPaginatedResponse<VisitApi>,
      {
        paginated: { page: number; pageSize: number };
        filters?: {
          createdAtFrom?: string;
          createdAtTo?: string;
          visitedAtFrom?: string;
          visitedAtTo?: string;
          status?: string;
          commerceId?: string;
          driverId?: string;
        };
      }
    >({
      query: ({ paginated, filters }) => ({
        url: '',
        params: {
          visitTypeId: 'PICKUP',
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          includeDocuments: true,
          ...(filters?.createdAtFrom && {
            createdAtFrom: filters.createdAtFrom,
          }),
          ...(filters?.createdAtTo && { createdAtTo: filters.createdAtTo }),
          ...(filters?.visitedAtFrom && {
            visitedAtFrom: filters.visitedAtFrom,
          }),
          ...(filters?.visitedAtTo && { visitedAtTo: filters.visitedAtTo }),
          ...(filters?.status && { status: filters.status }),
          ...(filters?.commerceId && { commerceId: filters.commerceId }),
          ...(filters?.driverId && { driverId: filters.driverId }),
        },
      }),
      providesTags: ['Visits'],
    }),
    getPickUpById: builder.query<VisitApi, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Visits', id: arg.id }],
    }),
    countPickUpsExport: builder.mutation<
      {
        count: number;
        limit: number;
        pickedShippingsCount: number;
        pickedShippingsLimit: number;
      },
      PickUpExportBody
    >({
      query: (body) => ({
        url: '/pickups/export-excel/count',
        method: 'POST',
        body,
      }),
    }),
    initiatePickUpsExport: builder.mutation<
      InitiateExportResponse,
      PickUpExportBody
    >({
      query: (body) => ({
        url: '/pickups/export-excel',
        method: 'POST',
        body,
      }),
    }),
    getPickUpsExportJobStatus: builder.query<ExportJob, string>({
      query: (jobId) => ({
        url: `/pickups/export-excel/jobs/${jobId}`,
      }),
    }),
  }),
});

export default visitsService;

export const {
  useGetVisitsByShippingNumberQuery,
  useGetPickUpsQuery,
  useGetPickUpByIdQuery,
  useCountPickUpsExportMutation,
  useInitiatePickUpsExportMutation,
  useLazyGetPickUpsExportJobStatusQuery,
} = visitsService;
